import JSZip from 'jszip';
import { adminFunctions } from '../firebase/adminFunctions';
import { downloadImageAsBlob } from './modelMediaDownload';

/**
 * Generates YOLO format annotations as an array of strings.
 * @param {Array} annotations Array of annotation objects.
 * @param {Array} features Array of feature objects.
 * @returns {Array<string>} Array of YOLO annotation strings.
 */
function generateYoloAnnotations(annotations, features) {
  const classIds = new Map();
  features.forEach((feature, index) => {
    classIds.set(feature.id, index);
  });

  return annotations.map(ann => {
    const classId = classIds.get(ann.original_class || ann.class);
    return `${classId} ${ann.x} ${ann.y} ${ann.width} ${ann.height}`;
  });
}

/**
 * Generates a data.yaml string for YOLO.
 * @param {Array} features Array of feature objects.
 * @returns {string} The data.yaml string.
 */
function generateYoloDataYaml(features) {
  const classNames = features.map(f => f.label);
  return `path: .  # Path is relative to the location of data.yaml
train: train/images  # Path to train images
val: valid/images    # Path to validation images

nc: ${classNames.length}  # Number of classes
names: [${classNames.map(name => `'${name}'`).join(', ')}]  # Class names
`;
}

/**
 * Generates COCO format annotations.
 * @param {Array} images Array of image objects with annotations.
 * @param {Array} features Array of feature objects.
 * @returns {Object} COCO annotation object.
 */
function generateCocoAnnotations(images, features) {
  const classIds = new Map();
  features.forEach((feature, index) => {
    classIds.set(feature.id, index + 1); // COCO class IDs start from 1
  });

  const coco = {
    info: {
      description: "Dataset exported from Megalith Maps",
      version: "1.0",
      year: new Date().getFullYear(),
      contributor: "",
      date_created: new Date().toISOString(),
    },
    licenses: [],
    images: [],
    annotations: [],
    categories: features.map(feature => ({
      id: classIds.get(feature.id),
      name: feature.label,
      supercategory: "object",
    })),
  };

  let annotationId = 1;
  images.forEach((image, imageId) => {
    coco.images.push({
      id: imageId + 1,
      file_name: image.filename,
      width: image.width,
      height: image.height,
      date_captured: "",
      license: 0,
      coco_url: "",
      flickr_url: "",
    });

    image.annotations.forEach(ann => {
      coco.annotations.push({
        id: annotationId++,
        image_id: imageId + 1,
        category_id: classIds.get(ann.original_class || ann.class),
        bbox: [
          ann.x * image.width - (ann.width * image.width) / 2,
          ann.y * image.height - (ann.height * image.height) / 2,
          ann.width * image.width,
          ann.height * image.height
        ],
        area: ann.width * image.width * ann.height * image.height,
        segmentation: [],
        iscrowd: 0,
      });
    });
  });

  return coco;
}

/**
 * Downloads all annotated images with progress tracking.
 * @param {Array} images Array of image objects.
 * @returns {Promise<{zip: Blob, errors: string[]}>} A Promise that resolves with the zip file and any errors.
 */
export async function downloadAnnotatedImages(images, onProgress) {
  const zip = new JSZip();
  const imagesFolder = zip.folder("images");
  const errors = [];
  let completed = 0;

  for (const image of images) {
    try {
      const imageBlob = await downloadImageAsBlob(image.url);
      imagesFolder.file(image.filename, imageBlob, { binary: true });
      completed++;
      console.log(`Downloaded ${completed}/${images.length}: ${image.filename}`);
      onProgress?.(completed);
    } catch (error) {
      console.error(`Error downloading ${image.filename}:`, error);
      errors.push(`Error downloading ${image.filename}: ${error.message}`);
    }
  }

  if (errors.length > 0) {
    zip.file("download_errors.txt", errors.join('\n'));
    console.log(`Download completed with ${errors.length} errors`);
  } else {
    console.log('All downloads completed successfully');
  }

  const blob = await zip.generateAsync({ type: "blob" });
  return { zip: blob, errors };
}

/**
 * Exports the dataset in YOLO format.
 * @param {Array} images Array of image objects with annotations.
 * @param {Array} features Array of feature objects.
 * @param {boolean} includeImages Whether to include images in the export.
 * @returns {Promise<Blob>} A Promise that resolves with the zip file as a Blob.
 */
export async function exportYoloDataset(images, features, includeImages = false, onProgress) {
  const zip = new JSZip();
  const trainFolder = zip.folder("train");
  const trainLabelsFolder = trainFolder.folder("labels");
  const trainImagesFolder = trainFolder.folder("images");
  const validFolder = zip.folder("valid");
  const validLabelsFolder = validFolder.folder("labels");
  const validImagesFolder = validFolder.folder("images");
  const errors = [];
  let completed = 0;

  // Split images 80/20 for training/validation
  const trainImages = images.slice(0, Math.floor(images.length * 0.8));
  const validImages = images.slice(Math.floor(images.length * 0.8));

  if (includeImages) {
    // Download training images
    for (const image of trainImages) {
      try {
      const imageBlob = await downloadImageAsBlob(image.url);
      trainImagesFolder.file(image.filename, imageBlob, { binary: true });
        completed++;
        console.log(`Downloaded train ${completed}/${images.length}: ${image.filename}`);
        onProgress?.(completed);
      } catch (error) {
        console.error(`Error downloading ${image.filename}:`, error);
        errors.push(`Error downloading ${image.filename}: ${error.message}`);
      }
    }

    // Download validation images
    for (const image of validImages) {
      try {
      const imageBlob = await downloadImageAsBlob(image.url);
      validImagesFolder.file(image.filename, imageBlob, { binary: true });
        completed++;
        console.log(`Downloaded valid ${completed}/${images.length}: ${image.filename}`);
        onProgress?.(completed);
      } catch (error) {
        console.error(`Error downloading ${image.filename}:`, error);
        errors.push(`Error downloading ${image.filename}: ${error.message}`);
      }
    }
  }

  // Add training labels
  for (const image of trainImages) {
    const yoloAnnotations = generateYoloAnnotations(image.annotations, features);
    const labelFilename = image.filename.substring(0, image.filename.lastIndexOf('.')) + ".txt";
    trainLabelsFolder.file(labelFilename, yoloAnnotations.join('\n'));
  }

  // Add validation labels
  for (const image of validImages) {
    const yoloAnnotations = generateYoloAnnotations(image.annotations, features);
    const labelFilename = image.filename.substring(0, image.filename.lastIndexOf('.')) + ".txt";
    validLabelsFolder.file(labelFilename, yoloAnnotations.join('\n'));
  }

  // Add data.yaml
  zip.file("data.yaml", generateYoloDataYaml(features));

  if (errors.length > 0) {
    zip.file("download_errors.txt", errors.join('\n'));
  }

  return zip.generateAsync({ type: "blob" });
}

/**
 * Exports the dataset in COCO format.
 * @param {Array} images Array of image objects with annotations.
 * @param {Array} features Array of feature objects.
 * @param {boolean} includeImages Whether to include images in the export.
 * @returns {Promise<Blob>} A Promise that resolves with the zip file as a Blob.
 */
export async function exportCocoDataset(images, features, includeImages = false, onProgress) {
  const zip = new JSZip();
  const errors = [];
  let completed = 0;

  if (includeImages) {
    const imagesFolder = zip.folder("images");
    for (const image of images) {
      try {
      const imageBlob = await downloadImageAsBlob(image.url);
      imagesFolder.file(image.filename, imageBlob, { binary: true });
        completed++;
        console.log(`Downloaded ${completed}/${images.length}: ${image.filename}`);
        onProgress?.(completed);
      } catch (error) {
        console.error(`Error downloading ${image.filename}:`, error);
        errors.push(`Error downloading ${image.filename}: ${error.message}`);
      }
    }
  }

  // Add annotations
  const cocoAnnotations = generateCocoAnnotations(images, features);
  zip.file("annotations.json", JSON.stringify(cocoAnnotations, null, 2));

  if (errors.length > 0) {
    zip.file("download_errors.txt", errors.join('\n'));
  }

  return zip.generateAsync({ type: "blob" });
}
